import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  Container,
  Alert,
  Grid,
} from '@mui/material';
import Editor from './Editor';

function EditPost() {
  const { id } = useParams();
  const history = useHistory();
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    categoryId: '',
    subcategoryId: '',
  });
  const [imageSettings, setImageSettings] = useState({
    width: 800,
    height: '',
    quality: 80
  });
  const [categories, setCategories] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchPost();
    fetchCategories();
  }, []);

  const fetchPost = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://jakakaomajka.com/backend';
      const response = await axios.get(`${apiUrl}/api/posts/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const post = response.data;
      setFormData({
        title: post.title,
        content: post.content,
        excerpt: post.excerpt || '',
        categoryId: post.categoryId,
        subcategoryId: post.subcategoryId || '',
      });
      if (post.featuredImage) {
        setPreviewUrl(`${process.env.REACT_APP_API_URL || 'https://jakakaomajka.com/backend'}/${post.featuredImage}`);
      }
    } catch (err) {
      setError('Greška pri učitavanju posta');
    }
  };

  const fetchCategories = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://jakakaomajka.com/backend';
      const response = await axios.get(`${apiUrl}/api/categories`);
      setCategories(response.data);
    } catch (err) {
      setError('Greška pri učitavanju kategorija');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContentChange = (content) => {
    setFormData(prev => ({
      ...prev,
      content: content || ''
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageSettingsChange = (e) => {
    const { name, value } = e.target;
    setImageSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const postData = new FormData();
    
    Object.keys(formData).forEach(key => {
      postData.append(key, formData[key]);
    });

    if (selectedFile) {
      postData.append('featuredImage', selectedFile);
      postData.append('imageWidth', imageSettings.width);
      postData.append('imageHeight', imageSettings.height || '');
      postData.append('imageQuality', imageSettings.quality);
    }

    try {
      const token = localStorage.getItem('adminToken');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://jakakaomajka.com/backend';
      await axios.patch(`${apiUrl}/api/posts/${id}`, postData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setSuccess(true);
      setTimeout(() => {
        history.replace('/', { refresh: true });
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Greška pri izmeni objave');
    }
  };

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Izmeni objavu
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Objava je uspešno izmenjena!
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Naslov"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Kratak opis"
            name="excerpt"
            value={formData.excerpt}
            onChange={handleInputChange}
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Kategorija</InputLabel>
            <Select
              name="categoryId"
              value={formData.categoryId}
              onChange={handleInputChange}
              required
            >
              {categories.map((category) => (
                <MenuItem key={category.slug} value={category.slug}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {formData.categoryId && categories.find(c => c.slug === formData.categoryId)?.subcategories?.length > 0 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Podkategorija</InputLabel>
              <Select
                name="subcategoryId"
                value={formData.subcategoryId}
                onChange={handleInputChange}
                required
              >
                {categories
                  .find(c => c.slug === formData.categoryId)
                  ?.subcategories.map((sub) => (
                    <MenuItem key={sub.slug} value={sub.slug}>
                      {sub.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Sadržaj
            </Typography>
            <Editor 
              value={formData.content}
              onChange={handleContentChange}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="featured-image-file"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="featured-image-file">
              <Button variant="contained" component="span">
                Promeni glavnu fotografiju
              </Button>
            </label>
            
            {selectedFile && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Podešavanja slike
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Širina (px)"
                      type="number"
                      name="width"
                      value={imageSettings.width}
                      onChange={handleImageSettingsChange}
                      InputProps={{ inputProps: { min: 100, max: 2000 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Visina (px)"
                      type="number"
                      name="height"
                      value={imageSettings.height}
                      onChange={handleImageSettingsChange}
                      InputProps={{ inputProps: { min: 100, max: 2000 } }}
                      helperText="Ostavite prazno za automatsku visinu"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Kvalitet (%)"
                      type="number"
                      name="quality"
                      value={imageSettings.quality}
                      onChange={handleImageSettingsChange}
                      InputProps={{ inputProps: { min: 1, max: 100 } }}
                    />
                  </Grid>
                </Grid>
                {previewUrl && (
                  <Box sx={{ mt: 2 }}>
                    <img
                      src={previewUrl}
                      alt="Preview"
                      style={{ maxWidth: '200px', maxHeight: '200px' }}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => history.push('/admin/dashboard')}
            >
              Otkaži
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
            >
              Sačuvaj izmene
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}

export default EditPost; 